import paths from 'constants/paths';
import { Article as ArticleType } from 'data/SingleArticle/types';
import { organizationJsonLd } from './organizationJsonLd';

const { basePath } = paths;

// Set by the Zapier content team, so they can easily sort and analyze data on the Parsely dashboard
export const parselyKeywordPrefixes = {
  childCategory: 'child-',
  integrationCategory: 'intcat-',
  mentions: 'ment-',
  persona: 'pers-',
  primaryIntegration: 'prim-',
  tag: 'tag-',
};
/**
 * https://schema.org/Article
 */
export const articleJsonLd = ({ article }: { article: ArticleType }) => {
  const UNKNOWN_SLUG = 'unknown';
  const headline = article.title;
  const authorName = article?.author?.[0]?.name;
  const personas = article.personas.map(
    (persona) => parselyKeywordPrefixes.persona + persona,
  );
  const integrationCategories = article.integrationCategories.map(
    (integrationCategory) =>
      parselyKeywordPrefixes.integrationCategory + integrationCategory,
  );
  const integrationsMentioned = article.integrationsMentioned
    ? article.integrationsMentioned.map(
        (integrationMentioned) =>
          parselyKeywordPrefixes.mentions + integrationMentioned,
      )
    : [];
  const primaryIntegration = article.primaryIntegration
    ? parselyKeywordPrefixes.primaryIntegration + article.primaryIntegration
    : parselyKeywordPrefixes.primaryIntegration + UNKNOWN_SLUG;
  const tags = article.tags.map((tag) => parselyKeywordPrefixes.tag + tag);
  // When a category HAS a parentCategory.slug, that means it is a child category
  const childCategory = article?.categories?.find(
    (category) => category.parentCategory?.slug,
  );
  const childCategorySlug = childCategory
    ? parselyKeywordPrefixes.childCategory + childCategory.categoryAssemblySlug
    : parselyKeywordPrefixes.childCategory + UNKNOWN_SLUG;
  // When a category DOES NOT HAVE a parentCategory.slug, that means it is a parent category
  const parentCategory = article?.categories?.find(
    (category) => !category.parentCategory?.slug,
  );
  const parentCategorySlug = parentCategory
    ? parentCategory.categoryAssemblySlug
    : UNKNOWN_SLUG;
  // Keywords that Parsely uses to train user profiles and that the content team uses to analyze blog traffic
  const parselyKeywords: string[] = [
    childCategorySlug,
    primaryIntegration,
    ...personas,
    ...integrationCategories,
    ...integrationsMentioned,
    ...tags,
    parentCategorySlug,
  ];

  return {
    '@context': 'http://schema.org',
    '@type': 'Article',
    headline,
    image: [article.image.url],
    dateModified: article.editorialLastPublishedDateComputed,
    datePublished: article.editorialOriginalPublishDate,
    author: {
      '@type': 'Person',
      name: authorName,
    },
    description: article.shortDescription,
    articleSection: parentCategorySlug || UNKNOWN_SLUG, // article's parent category is the `section` in the Parsely dashboard
    keywords: parselyKeywords,
    mainEntityOfPage: `https://zapier.com${basePath}/${article.slug}`,
    publisher: organizationJsonLd,
    name: headline,
  };
};
